import React from 'react';
import PropTypes from 'prop-types';

import Cta from '../../atoms/cta/cta';
import Text from '../../atoms/text/text';
import {
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  colorSchemaList,
  ARROW_POSITION_RIGHT,
  COLOR_SCHEMA_LIGHT,
  componentSizeList,
  COMPONENT_SIZE_RESPONSIVE,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames, IClassNames, applyColorSchema, getMappedColor } from '../../../utils/styles';
import './card.scss';

export interface ICard extends IComponent {
  cta?: string;
  ctaLink?: string;
  title?: string;
  subTitle?: string;
  ctaMobile?: boolean;
  ctaMobileWhite?: boolean;
}

const TEXT_COLOR_SCHEMA_DARK: Color = 'light';
const TEXT_COLOR_SCHEMA_LIGHT: Color = 'blue';

const getColorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: TEXT_COLOR_SCHEMA_DARK,
    [COLOR_SCHEMA_DARK_LIGHT]: TEXT_COLOR_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: TEXT_COLOR_SCHEMA_LIGHT,
  };
};

const classNames = ({ colorSchema }: IClassNames): IClassNames =>
  makeClassNames({
    card: [`card`, `${applyColorSchema('card', colorSchema)}`],
    titleBox: 'card__title-box',
    title: 'card__title',
    subTitle: 'card__sub-title',
    cta: 'card__cta',
    content: 'card__content',
  });

const Card: React.FC<ICard> = ({
  /* istanbul ignore next: default value */ colorSchema = COLOR_SCHEMA_DARK,
  /* istanbul ignore next: default value */ size = COMPONENT_SIZE_RESPONSIVE,
  /* istanbul ignore next: default value */ ctaMobile = false,
  /* istanbul ignore next: default value */ ctaMobileWhite = false,
  trackingData = {},
  children,
  cta,
  ctaLink,
  title,
  subTitle,
  className,
}) => {
  const cn = classNames({ colorSchema });
  const hasCta = Boolean(cta && ctaLink);
  // const hasTitle = Boolean(title);
  const colorSchemaMapping = getColorSchemaMapping();
  const color = getMappedColor({ colorSchemaMapping, colorSchema });
  return (
    <div className={[cn.card, className].filter(Boolean).join(' ')} data-size={size}>
      <React.Fragment>
        {title && (
          <div className={cn.titleBox}>
            <Text className={cn.title} color={color} tag="h3" fontSize="22" fontWeight="500">
              {title}
            </Text>
            {subTitle && (
              <Text className={cn.subTitle} color={color} tag="h4" fontSize="14" fontWeight="500">
                {subTitle}
              </Text>
            )}
          </div>
        )}
        <div className={cn.content}>{children}</div>
        {hasCta && (
          <div className={cn.cta}>
            <Cta
              href={ctaLink}
              iconPosition={ARROW_POSITION_RIGHT}
              colorSchema={colorSchema}
              mobileButton={ctaMobile}
              isMobileButtonWhite={ctaMobileWhite}
              trackingData={trackingData}
            >
              {cta}
            </Cta>
          </div>
        )}
      </React.Fragment>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  Card.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    colorSchema: PropTypes.oneOf(colorSchemaList),
    cta: PropTypes.string,
    ctaLink: PropTypes.string,
    ctaMobile: PropTypes.bool,
    ctaMobileWhite: PropTypes.bool,
    size: PropTypes.oneOf(componentSizeList),
    title: PropTypes.string,
  };
}

export default Card;
